import {
  useGetFrontRichiesteIdPreventivi,
  usePostFrontRichiesteIdAzioniIntegra,
} from '@framework/api-client';
import { getNetworkErrorMessage, useCurrentUser } from '@framework/auth';
import {
  SubmissionCardTabsPrefix,
  SubmissionResponsiveCard,
  TitleSubmissionRow,
} from '@framework/common-web';
import {
  DelegationMachine,
  DownloadConsolidatedSubmissionButton,
  SubmissionBurdenLogic,
  SubmissionLogic,
  SubmissionStateTag,
  useSubmission,
  useSubmissionModules,
} from '@framework/frontoffice-submission';
import { useSubmissionMenuItems } from '@framework/frontoffice-submission/src/submission/hooks/useSubmissionMenuItems';
import { useTranslation } from '@framework/i18n';
import {
  generatePath,
  LinkButton,
  useBreadcrumbItem,
  useHistory,
  useIdFromUrl,
  type BrandedPath,
} from '@framework/ui';
import { Alert, message, Space, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { type RouteComponentProps } from 'react-router';
import { useTheme } from 'styled-components';
import { getSubmissionMenuLink } from '../../components/submissions/hooks/getSubmissionMenuLink';
import { PageLayout } from '../../ui/layout/PageLayout';
import { LoadingPage } from '../loading/LoadingPage';
import { ContentCardWrapper } from './components/ContentCardWrapper';
import { IntegrationConfirmButton } from './components/IntegrationConfirmButton';
import { getPrincipalModule } from './components/principal-module-redirector/getPrincipalModule';
import { SubmissionCloneButton } from './components/SubmissionDuplicateButton';
import { SubmissionLinkedBox } from './components/SubmissionLinkedBox';
import { useDelegationData } from './delegation/hooks/useDelegationData';
import { getSubmissionSentTabList } from './sent/getSubmissionSentTabList';
import { IntegrationBanner } from './sent/IntegrationBanner';
import { SubmissionSentTabCommunications } from './sent/SubmissionSentTabCommunications';
import { SubmissionSentTabIntegrations } from './sent/SubmissionSentTabIntegrations';
import { SubmissionSentTabPayments } from './sent/SubmissionSentTabPayments';
import { SubmissionSentTabReceipt } from './sent/SubmissionSentTabReceipt';
import { SubmissionSentTabRequest } from './sent/SubmissionSentTabRequest';
import { useSubmissionsIntegrationsState } from './sent/useSubmissionsIntegrationsState';
import { useGenerateAfterSendBurdenPayments } from './utils/useGenerateAfterSendBurdenPayments';

const { Text, Title } = Typography;

interface Params {
  id: string;
  moduleId: string;
}

export interface ISubmissionSentPageProps extends RouteComponentProps<Params> {}

/**
 * Pagina della richiesta dopo l'invio.
 */
export function SubmissionSentPage(props: ISubmissionSentPageProps) {
  const [submissionId] = useIdFromUrl(props, 'id');
  const [submissionModuleId] = useIdFromUrl(props, 'moduleId');

  const history = useHistory();
  const { t } = useTranslation();

  const submissionData = useSubmission(submissionId);
  const currentUser = useCurrentUser();

  const submissionModules = useSubmissionModules({ submissionId });

  // Recupero i preventivi collegati alla richiesta
  const quotesResult = useGetFrontRichiesteIdPreventivi(
    submissionId ?? '<unknown>',
    { query: { enabled: Boolean(submissionId) } },
  );

  const submissionMenuItems = useSubmissionMenuItems({
    modules: submissionModules.modules,
    submissionId,
    submission: submissionData.submission,
    getMenuLink: getSubmissionMenuLink,
  });

  const { delegationData } = useDelegationData({
    modules: submissionModules.modules,
    applicant: submissionData?.submission?.datiTitolari,
    submissionId,
  });

  const { submission, inquiry, title, submissionRequest, inquiryRequest } =
    submissionData;

  // Estraggo l'id del modulo principale per usarlo come modulo iniziale
  // se non è specificato un modulo nell'url
  const initialModuleId = useMemo(
    () =>
      getPrincipalModule(submissionModules.modules)?.id ??
      submissionModules.modules?.[0]?.id,
    [submissionModules.modules],
  );

  const startIntegration = usePostFrontRichiesteIdAzioniIntegra();

  // Breadcrumbs
  useBreadcrumbItem({
    path: '/richieste',
    title: t('submission-sent-form.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: inquiryRequest.data?.titolo ?? '',
  });

  const [activeTab, setActiveTab] = useState<string>('receipt');
  const onTabChange = (key: string) => {
    setActiveTab(key);
  };

  const isLoading =
    submissionRequest.isLoading ||
    inquiryRequest.isLoading ||
    submissionModules.isLoading ||
    quotesResult.isLoading;
  const error =
    submissionRequest.error ?? inquiryRequest.error ?? submissionModules.error;

  const sentLayout = useMemo(
    () => inquiryRequest.data?.postCompilazione?.inviato,
    [inquiryRequest.data?.postCompilazione?.inviato],
  );
  const theme = useTheme();

  const shouldShowCommunicationsTab = SubmissionLogic.canShowCommunications(
    submission?.stato,
  );

  const shouldShowPaymentsTab = SubmissionBurdenLogic.showPayments(
    submission?.statoOneri,
    submission?.statoPreventivi,
  );

  // Gestione delle integrazioni
  const {
    isIntegrated,
    canIntegrate,
    hasIntegrations,
    hasIntegrationDrafts,
    shouldShowIntegrationsTab,
    draftIntegrationId,
  } = useSubmissionsIntegrationsState(inquiry, submission, delegationData);

  const handleStartIntegration = useCallback(async () => {
    try {
      const result = await startIntegration.mutateAsync({
        id: submission!.id!,
      });

      message.success(
        t(
          'submission-sent-form.integrazione-creata-con-successo',
          `Integrazione creata con successo`,
        ),
      );
      history.push(
        generatePath(`/richieste/:id`, {
          id: result.id!,
        }),
      );
    } catch (e) {
      console.error(e);
      message.error(getNetworkErrorMessage(e, t("submission-sent-form.errore-in-integrazione-richiesta",`Si è verificato un errore durante l'integrazione della richiesta, si prega di riprovare.`),)); // prettier-ignore
    }
  }, [submission, startIntegration, history]);

  const handleConfirmIntegration = useCallback(() => {
    handleStartIntegration();
  }, [handleStartIntegration]);

  // Richiamo il custom hook per la generazione automatica dei pagamenti dopo l'invio
  useGenerateAfterSendBurdenPayments(submissionId);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <PageLayout>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-sent-form.errore-in-caricamento-domanda',
            'Si è verificato un errore durante il caricamento della domanda.',
          )}
          description={getNetworkErrorMessage(
            error,
            t(
              'submission-sent-form.errore-in-caricamento-richiesta',
              'Si è verificato un errore durante il caricamento della richiesta.',
            ),
          )}
        />
      </PageLayout>
    );
  }

  if (!submissionId) {
    return (
      <PageLayout>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-sent-form.errore-in-caricamento-domanda',
            'Si è verificato un errore durante il caricamento della domanda.',
          )}
          description={t(
            'submission-sent-form.id-della-richiesta-non-valido',
            'ID della richiesta non valido',
          )}
        />
      </PageLayout>
    );
  }

  // in caso di richiesta non inviata
  if (!SubmissionLogic.isSubmissionSent(submission) && submissionId) {
    history.replace(generatePath('/richieste/:id', { id: submissionId }));
  }

  const showCloneButton =
    !inquiryRequest.data?.flRichiestaUnivoca &&
    DelegationMachine.can(delegationData, '.canDuplicate', currentUser).allowed;

  const showDownloadConsolidated = SubmissionLogic.canDownloadConsolidatedPdf(
    submission,
    inquiry,
    submissionModules.modules,
  );

  return (
    <PageLayout>
      <SubmissionLinkedBox submission={submission} inquiry={inquiry} />
      <ContentCardWrapper submission={submission}>
        <SubmissionResponsiveCard
          title={
            <TitleSubmissionRow
              // afterTitle={<SubmissionLinkedBox submission={submission} />}
              stateTag={
                <Space>
                  <SubmissionStateTag submission={submission} />
                  {/* {submission?.numeroProcedimento && (
                    <Space>
                      <Text style={{ fontWeight: 'normal' }}>
                        {t(
                          'submission-sent-form.procedimento',
                          'Procedimento:',
                        )}{' '}
                      </Text>
                      <Text>{submission?.numeroProcedimento}</Text>
                    </Space>
                  )} */}
                </Space>
              }
              right={
                <Space>
                  {showDownloadConsolidated && (
                    <DownloadConsolidatedSubmissionButton
                      submissionId={submissionId}
                    />
                  )}
                  {showCloneButton && (
                    <SubmissionCloneButton
                      isJustCreated={false}
                      submissionId={submissionId}
                    />
                  )}
                </Space>
              }
            >
              {title}
            </TitleSubmissionRow>
          }
          cover={
            hasIntegrations ? (
              <IntegrationBanner
                integrationID={null}
                message={
                  isIntegrated
                    ? t(
                        'submission-sent-form.richiesta-integrata',
                        'Questa richiesta è stata integrata.',
                      )
                    : hasIntegrationDrafts
                    ? t(
                        'submission-sent-form.integrazione-in-bozza',
                        'È presente una bozza di integrazione per questa richiesta.',
                      )
                    : t(
                        'submission-sent-form.integrazione-di-un-altra-richiesta',
                        "Questa è un'integrazione di un'altra richiesta.",
                      )
                }
                handleShowIntegrations={
                  activeTab !== 'integrations'
                    ? () => onTabChange('integrations')
                    : undefined
                }
              />
            ) : null
          }
          extra={
            <>
              {canIntegrate && (
                <IntegrationConfirmButton
                  onConfirm={handleConfirmIntegration}
                />
              )}
              {hasIntegrationDrafts && draftIntegrationId && (
                <LinkButton
                  type="primary"
                  size="large"
                  to={generatePath('/richieste/:id', {
                    id: draftIntegrationId,
                  })}
                >
                  {t(
                    'submission-sent-form.apri-integrazione-in-bozza',
                    'Apri Integrazione in Bozza',
                  )}
                </LinkButton>
              )}
            </>
          }
          tabList={getSubmissionSentTabList({
            shouldShowCommunicationsTab,
            shouldShowIntegrationsTab,
            shouldShowPaymentsTab,
          })}
          tabProps={{
            'aria-label': t(
              'submission-sent-form.tabs-row.aria',
              'Navigazione tra le schede della richiesta inviata',
            ),
            id: `${SubmissionCardTabsPrefix}main`,
          }}
          activeTabKey={activeTab}
          onTabChange={onTabChange}
        >
          {/* Ricevuta Invio */}
          <SubmissionSentTabReceipt
            active={activeTab === 'receipt'}
            submission={submission}
            sentLayout={sentLayout}
          />
          {/* Richiesta in modalità read only */}
          <SubmissionSentTabRequest
            active={activeTab === 'request'}
            menuItems={submissionMenuItems.menuItems}
            initialModuleId={initialModuleId}
            submissionModuleId={submissionModuleId}
            submissionModules={submissionModules.modules}
            submission={submission}
          />
          {/* Comunicazioni */}
          {shouldShowCommunicationsTab && (
            <SubmissionSentTabCommunications
              active={activeTab === 'communications'}
              submission={submission}
            />
          )}
          {/* Integrazioni */}
          {shouldShowIntegrationsTab && (
            <SubmissionSentTabIntegrations
              active={activeTab === 'integrations'}
              submission={submission}
            />
          )}
          {/* Pagamenti */}
          {shouldShowPaymentsTab && (
            <SubmissionSentTabPayments
              active={activeTab === 'payments'}
              submission={submission}
            />
          )}
        </SubmissionResponsiveCard>
      </ContentCardWrapper>
    </PageLayout>
  );
}
